"use client"

import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import { PlusCircle, Trash2, Copy, Save, Eye, ArrowLeft } from "lucide-react"
import { menuData as initialMenuData } from "../data/menu-data"
import MenuSection from "../components/MenuSection"
import Button from "../components/Button"
import Input from "../components/Input"
import Label from "../components/Label"
import Switch from "../components/Switch"
import { Card, CardHeader, CardTitle, CardContent } from "../components/Card"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/Tabs"
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/Select"

function EditorPage() {
  console.log("EditorPage rendering")
  console.log("Initial menu data:", initialMenuData)

  const [menuData, setMenuData] = useState([])
  const [activeTab, setActiveTab] = useState("editor")
  const [generatedCode, setGeneratedCode] = useState("")
  const [toast, setToast] = useState({ visible: false, title: "", message: "" })

  useEffect(() => {
    console.log("EditorPage useEffect running")
    try {
      // Deep clone the initial menu data to avoid reference issues
      const clonedData = JSON.parse(JSON.stringify(initialMenuData))
      console.log("Cloned data:", clonedData)
      setMenuData(clonedData)
    } catch (error) {
      console.error("Error in useEffect:", error)
    }
  }, [])

  const showToast = (title, message) => {
    setToast({ visible: true, title, message })
    setTimeout(() => {
      setToast({ visible: false, title: "", message: "" })
    }, 3000)
  }

  const handleSectionTitleChange = (index, title) => {
    const updatedMenu = [...menuData]
    updatedMenu[index].title = title
    setMenuData(updatedMenu)
  }

  const handleSectionColorChange = (index, color) => {
    const updatedMenu = [...menuData]
    updatedMenu[index].color = color
    setMenuData(updatedMenu)
  }

  const handleItemChange = (sectionIndex, itemIndex, field, value) => {
    const updatedMenu = [...menuData]
    updatedMenu[sectionIndex].items[itemIndex] = {
      ...updatedMenu[sectionIndex].items[itemIndex],
      [field]: value,
    }
    setMenuData(updatedMenu)
  }

  const addNewItem = (sectionIndex) => {
    const updatedMenu = [...menuData]
    updatedMenu[sectionIndex].items.push({
      name: "New Item",
      price: "0/-",
    })
    setMenuData(updatedMenu)
  }

  const removeItem = (sectionIndex, itemIndex) => {
    const updatedMenu = [...menuData]
    updatedMenu[sectionIndex].items.splice(itemIndex, 1)
    setMenuData(updatedMenu)
  }

  const addNewSection = () => {
    setMenuData([
      ...menuData,
      {
        title: "NEW SECTION",
        color: "green",
        items: [
          {
            name: "New Item",
            price: "0/-",
          },
        ],
      },
    ])
  }

  const removeSection = (index) => {
    const updatedMenu = [...menuData]
    updatedMenu.splice(index, 1)
    setMenuData(updatedMenu)
  }

  const generateJavaScriptCode = () => {
    // Format the menu data as JavaScript code
    const formattedData = JSON.stringify(menuData, null, 2)
      .replace(/"([^"]+)":/g, "$1:") // Remove quotes from property names
      .replace(/"([^"]+)"/g, '"$1"') // Keep quotes around string values

    const code = `export const menuData = ${formattedData}`
    setGeneratedCode(code)
    setActiveTab("code")
    showToast("Code Generated", "Copy the code and replace your menu-data.js file")
  }

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedCode)
    showToast("Copied to clipboard", "The code has been copied to your clipboard")
  }

  // Add a loading state with debug information
  if (menuData.length === 0) {
    return (
      <div className="p-8">
        <h2 className="text-2xl font-bold mb-4">Loading Editor...</h2>
        <p className="mb-2">If this screen persists, there might be an issue with loading the menu data.</p>
        <div className="bg-gray-100 p-4 rounded">
          <p className="font-bold">Debug Information:</p>
          <p>Menu Data Length: {menuData.length}</p>
          <p>Initial Menu Data Available: {initialMenuData ? "Yes" : "No"}</p>
          <p>Initial Menu Data Length: {initialMenuData ? initialMenuData.length : "N/A"}</p>
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto p-4 max-w-6xl">
      {toast.visible && (
        <div className="fixed top-4 right-4 bg-white shadow-lg rounded-lg p-4 z-50 max-w-md">
          <h4 className="font-bold">{toast.title}</h4>
          <p>{toast.message}</p>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center">
          <Link to="/" className="mr-4">
            <Button variant="outline" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back to Menu
            </Button>
          </Link>
          <h1 className="text-3xl font-bold">Chai Baghan Menu Editor</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setActiveTab("preview")}>
            <Eye className="mr-2 h-4 w-4" /> Preview
          </Button>
          <Button onClick={generateJavaScriptCode}>
            <Save className="mr-2 h-4 w-4" /> Generate Code
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="editor">Editor</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
          <TabsTrigger value="code">Generated Code</TabsTrigger>
        </TabsList>

        <TabsContent value="editor" className="mt-4">
          <div className="space-y-6">
            {menuData.map((section, sectionIndex) => (
              <Card key={sectionIndex} className="overflow-hidden">
                <CardHeader className="bg-slate-50">
                  <div className="flex justify-between items-center">
                    <div className="flex-1">
                      <Input
                        value={section.title}
                        onChange={(e) => handleSectionTitleChange(sectionIndex, e.target.value)}
                        className="font-bold text-lg"
                      />
                    </div>
                    <div className="flex items-center gap-4 ml-4">
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`color-${sectionIndex}`}>Color:</Label>
                        <Select
                          value={section.color}
                          onValueChange={(value) => handleSectionColorChange(sectionIndex, value)}
                        >
                          <SelectTrigger className="w-24">
                            <SelectValue placeholder="Color" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="green">Green</SelectItem>
                            <SelectItem value="black">Black</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <Button variant="destructive" size="sm" onClick={() => removeSection(sectionIndex)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    {section.items.map((item, itemIndex) => (
                      <div key={itemIndex} className="grid grid-cols-12 gap-4 items-start border-b pb-4">
                        <div className="col-span-5">
                          <Label htmlFor={`name-${sectionIndex}-${itemIndex}`}>Name</Label>
                          <Input
                            id={`name-${sectionIndex}-${itemIndex}`}
                            value={item.name}
                            onChange={(e) => handleItemChange(sectionIndex, itemIndex, "name", e.target.value)}
                          />
                        </div>
                        <div className="col-span-2">
                          <Label htmlFor={`price-${sectionIndex}-${itemIndex}`}>Price</Label>
                          <Input
                            id={`price-${sectionIndex}-${itemIndex}`}
                            value={item.price}
                            onChange={(e) => handleItemChange(sectionIndex, itemIndex, "price", e.target.value)}
                          />
                        </div>
                        <div className="col-span-3">
                          <Label htmlFor={`description-${sectionIndex}-${itemIndex}`}>Description</Label>
                          <Input
                            id={`description-${sectionIndex}-${itemIndex}`}
                            value={item.description || ""}
                            onChange={(e) => handleItemChange(sectionIndex, itemIndex, "description", e.target.value)}
                          />
                        </div>
                        <div className="col-span-1 flex flex-col items-center justify-end h-full pt-6">
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`special-${sectionIndex}-${itemIndex}`}
                              checked={item.isSpecial || false}
                              onCheckedChange={(checked) =>
                                handleItemChange(sectionIndex, itemIndex, "isSpecial", checked)
                              }
                            />
                            <Label htmlFor={`special-${sectionIndex}-${itemIndex}`}>Special</Label>
                          </div>
                        </div>
                        <div className="col-span-1 flex items-end justify-end h-full">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeItem(sectionIndex, itemIndex)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline" onClick={() => addNewItem(sectionIndex)} className="w-full">
                      <PlusCircle className="mr-2 h-4 w-4" /> Add Item
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            <Button onClick={addNewSection} className="w-full">
              <PlusCircle className="mr-2 h-4 w-4" /> Add New Section
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="preview" className="mt-4">
          <div className="bg-amber-50 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-6 text-center">Menu Preview</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {menuData.map((section) => (
                <MenuSection
                  key={section.title}
                  title={section.title}
                  items={section.items}
                  highlight={section.highlight}
                  color={section.color}
                />
              ))}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="code" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Generated JavaScript Code</CardTitle>
              <Button variant="outline" size="sm" onClick={copyToClipboard}>
                <Copy className="mr-2 h-4 w-4" /> Copy
              </Button>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-950 text-slate-50 p-4 rounded-md overflow-auto max-h-[60vh]">
                <pre className="whitespace-pre-wrap">{generatedCode}</pre>
              </div>
              <p className="mt-4 text-sm text-muted-foreground">
                Copy this code and replace the contents of your menu-data.js file to update your menu.
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default EditorPage
